import React, { forwardRef, useContext, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Grow from '@material-ui/core/Grow';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CategoryIcon from '@material-ui/icons/Category';
import CheckIcon from '@material-ui/icons/Check';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { SnackbarContext, SnackbarVariant } from '@eas/common-web';
import { updateWorkOrder } from '@modules/work-order/work-order-api';
import { WebsocketCacheControlContext } from '@composite/websocket/websocket-cache-control-context';
import { WorkOrder } from '@models';

type MenuItemProps = {
  closeMenu: () => void;
  workOrder: WorkOrder;
  afterSubmit: () => void;
};

export const useStyles = makeStyles((theme) => ({
  moreIcon: {
    color: theme.palette.grey[500],
    display: 'flex',
    fontSize: '16px',
  },
  spacer: {
    width: 22,
    marginRight: 8,
    marginLeft: 8,
    flexShrink: 0,
  },
}));

export const ExpectedMaterialSubmenuList = ({
  workOrder,
  handleOnItemClick,
}: {
  workOrder?: WorkOrder;
  handleOnItemClick: (expectedMaterial: boolean) => void;
}) => {
  const classes = useStyles();

  return (
    <MenuList disablePadding={true}>
      <MenuItem
        onClick={() => {
          handleOnItemClick(true);
        }}
      >
        <div className={classes.spacer}>
          {workOrder?.expectedMaterial ? <CheckIcon /> : <></>}
        </div>
        Áno
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleOnItemClick(false);
        }}
      >
        <div className={classes.spacer}>
          {workOrder && !workOrder?.expectedMaterial ? <CheckIcon /> : <></>}
        </div>
        Nie
      </MenuItem>
    </MenuList>
  );
};

export const ExpectedMaterialMenuItem = forwardRef<
  HTMLLIElement,
  MenuItemProps
>(function ExpectedMaterialMenuItem({ closeMenu, workOrder, afterSubmit }) {
  const anchorEl = useRef(null);
  const [open, setOpen] = useState(false);

  const classes = useStyles();

  const { showSnackbar } = useContext(SnackbarContext);
  const wsCache = useContext(WebsocketCacheControlContext);
  const clearWSCache = wsCache?.['table']?.current;

  return (
    <>
      <MenuItem
        ref={anchorEl}
        onMouseEnter={() => {
          setOpen(true);
        }}
        onMouseLeave={() => {
          setOpen(false);
        }}
      >
        <ListItemIcon>
          <CategoryIcon fontSize="small" />
        </ListItemIcon>
        <FormattedMessage
          id="WORK_ORDER_MENU_ITEM_TODO"
          defaultMessage="Očakávaný materiál"
        />
        <ListItemSecondaryAction>
          <PlayArrowIcon className={classes.moreIcon} />
        </ListItemSecondaryAction>
      </MenuItem>
      <Popper
        open={open}
        anchorEl={anchorEl.current}
        placement="right-end"
        style={{ zIndex: 1300 }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }}>
            <Paper
              elevation={20}
              onMouseEnter={() => {
                setOpen(true);
              }}
              onMouseLeave={() => {
                setOpen(false);
              }}
            >
              <ExpectedMaterialSubmenuList
                workOrder={workOrder}
                handleOnItemClick={async (expectedMaterial) => {
                  const resp = await updateWorkOrder(workOrder?.id, {
                    ...workOrder,
                    expectedMaterial,
                  }).response;

                  if (resp.ok) {
                    afterSubmit?.();
                    clearWSCache?.();
                    closeMenu();
                  } else {
                    showSnackbar(
                      'Atribút "Očakávaný materiál" sa nepodarilo zmeniť.',
                      SnackbarVariant.ERROR
                    );
                  }
                }}
              />
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
});
